import { Module, MutationTree, ActionTree, GetterTree } from "vuex"
import { RootState } from "@/store"
import { NewLocation } from "@/types/setup"
import axios from "axios"
import { Country } from "@/types/country"
import { Region } from "@/types/region"
import { Timezone } from "@/types/timezone"
import { User } from "@/types/user"
import { Location } from "@/types/setup"

export interface LocationState {
    newLocation: NewLocation
    existingLocation?: Location
    companyId?: string
    loading: boolean
    countries: Country[]
    regions: Region[]
    timezones: Timezone[]
    owners: User[]
    loadingOwners: boolean
    locationGroupId?: string
}

export const defaultNewLocation: NewLocation = {
    name: "",
    country: null,
    region: null,
    timezone: null,
    ownerId: null,
    coordinates: { latitude: null, longitude: null },
}

const state: LocationState = {
    newLocation: { ...defaultNewLocation },
    existingLocation: undefined,
    companyId: undefined,
    loading: false,
    countries: [],
    regions: [],
    timezones: [],
    owners: [],
    loadingOwners: false,
    locationGroupId: undefined,
}

const mutations: MutationTree<LocationState> = {
    updateNewLocation(state, payload: Partial<NewLocation>) {
        state.newLocation = { ...state.newLocation, ...payload }
    },
    setExistingLocation(state, location: Location) {
        state.existingLocation = location
    },
    setCompanyId(state, companyId: string) {
        state.companyId = companyId
    },
    setCountries(state, countries: Country[]) {
        state.countries = countries
    },
    setLoading(state, loading: boolean) {
        state.loading = loading
    },
    setRegions(state, regions: Region[]) {
        state.regions = regions
    },
    setTimezones(state, timezones: Timezone[]) {
        state.timezones = timezones
    },
    setOwners(state, owners: User[]) {
        state.owners = owners
    },
    setLoadingOwners(state, loading: boolean) {
        state.loadingOwners = loading
    },
    setLocationGroupId(state, locationGroupId: string) {
        state.locationGroupId = locationGroupId
    },
}

const actions: ActionTree<LocationState, RootState> = {
    async fetchCountries({ commit }) {
        commit("setLoading", true)

        try {
            const response = await axios.get("/countries?visible=true")

            if (response.status !== 200) {
                throw new Error("Failed to fetch countries")
            }

            commit("setCountries", response.data.data.countries)
        } catch (error) {
            console.log(error)
        } finally {
            commit("setLoading", false)
        }
    },
    async fetchRegions({ commit }) {
        commit("setLoading", true)

        try {
            const response = await axios.get("/pricing/spot-market/regions")

            if (response.status !== 200) {
                throw new Error("Failed to fetch regions")
            }

            commit("setRegions", response.data.data.entries)
        } catch (error) {
            console.log(error)
        } finally {
            commit("setLoading", false)
        }
    },
    async fetchTimezones({ commit }, countryCode: string) {
        commit("setLoading", true)

        try {
            const response = await axios.get(`/timezones?countryCode=${countryCode}`)

            if (response.status !== 200) {
                throw new Error("Failed to fetch timezones")
            }

            commit("setTimezones", response.data.data.timezones)
        } catch (error) {
            console.log(error)
        } finally {
            commit("setLoading", false)
        }
    },
    async fetchOwners({ commit }, companyId: string) {
        commit("setLoadingOwners", true)
        try {
            const response = await axios.get(`/setup/companies/${companyId}/available-users`)
            commit("setOwners", response.data.data.users)
        } catch (error) {
            console.log(error)
        } finally {
            commit("setLoadingOwners", false)
        }
    },
    resetLocation({ commit }) {
        commit("updateNewLocation", {
            name: "",
            country: null,
            region: null,
            timezone: null,
            ownerId: null,
            coordinates: { latitude: null, longitude: null },
        })
    },
    setExistingLocation({ commit }, location: Location) {
        commit("setExistingLocation", location)
    },
    setCompanyId({ commit }, companyId: string) {
        commit("setCompanyId", companyId)
    },
    setLocationGroupId({ commit }, locationGroupId: string) {
        commit("setLocationGroupId", locationGroupId)
    },
}

const getters: GetterTree<LocationState, RootState> = {
    newLocation: (state) => state.newLocation,
    countries: (state) => state.countries,
    regions: (state) => state.regions,
    timezones: (state) => state.timezones,
    loading: (state) => state.loading,
    owners: (state) => {
        if (!state.owners) return []
        return (
            [...state.owners].sort((a: User, b: User) => {
                if (!a || !a.fullName) {
                    return -1
                }

                if (!b || !b.fullName) {
                    return 1
                }

                return a.fullName.localeCompare(b.fullName)
            }) || []
        )
    },
    loadingOwners: (state) => state.loadingOwners,
    canContinue: (state) => {
        return !!(
            state.newLocation.name &&
            state.newLocation.country &&
            state.newLocation.region &&
            state.newLocation.timezone
        )
    },
    existingLocation: (state) => state.existingLocation,
    companyId: (state) => state.companyId,
    locationGroupId: (state) => state.locationGroupId,
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<LocationState, RootState>
